import React from 'react';
import { AlertTriangle, CheckCircle, Download, Heart } from 'lucide-react';

const WellbeingTab = ({ ctx }) => {
  const { summary, members, pastoral, oceanAggregates, alerts, alertSummary, API_URL, getToken, setAlerts, setAlertSummary } = ctx;

  return (
<div data-testid="pastoral-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Pastoral Overview</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Aggregated mood indicators for your members. Individual conversations are never shown.
  </p>

  {/* Wellbeing Alerts Banner */}
  {(alertSummary.urgent > 0 || alertSummary.concern > 0) && (
    <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: alertSummary.urgent > 0 ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${alertSummary.urgent > 0 ? '#FECACA' : '#FEF3C7'}` }} data-testid="wellbeing-alerts-banner">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} style={{ color: alertSummary.urgent > 0 ? '#DC2626' : '#F59E0B' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Wellbeing Alerts</h3>
        </div>
        <div className="flex gap-2">
          {alertSummary.urgent > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#DC2626', color: 'white' }}>{alertSummary.urgent} Urgent</span>}
          {alertSummary.concern > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#F59E0B', color: 'white' }}>{alertSummary.concern} Concern</span>}
          {alertSummary.watch > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#6B7280', color: 'white' }}>{alertSummary.watch} Watch</span>}
        </div>
      </div>
      <div className="space-y-2">
        {alerts.filter(a => !a.acknowledged).slice(0, 5).map(a => {
          const sevColors = { urgent: { bg: '#FEE2E2', border: '#FECACA', color: '#991B1B' }, concern: { bg: '#FEF3C7', border: '#FDE68A', color: '#92400E' }, watch: { bg: '#F3F4F6', border: '#E5E7EB', color: '#4B5563' } };
          const sc = sevColors[a.severity] || sevColors.watch;
          return (
            <div key={a.alert_id} className="flex items-center justify-between rounded-lg px-3 py-2.5" style={{ backgroundColor: sc.bg, border: `1px solid ${sc.border}` }} data-testid={`alert-${a.alert_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase" style={{ backgroundColor: sc.color, color: 'white' }}>{a.severity}</span>
                  <span className="text-xs font-semibold truncate" style={{ color: '#1A1209' }}>{a.title}</span>
                </div>
                <p className="text-[10px]" style={{ color: sc.color }}>{a.description}</p>
              </div>
              <button
                onClick={async () => {
                  await fetch(`${API_URL}/api/corporate/wellbeing/alerts/${a.alert_id}/acknowledge`, {
                    method: 'PUT', headers: { Authorization: `Bearer ${getToken()}` },
                  });
                  setAlerts(prev => prev.map(x => x.alert_id === a.alert_id ? { ...x, acknowledged: true } : x));
                  setAlertSummary(prev => ({ ...prev, [a.severity]: Math.max(0, prev[a.severity] - 1) }));
                }}
                className="ml-3 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold flex-shrink-0"
                style={{ backgroundColor: 'white', color: sc.color, border: `1px solid ${sc.border}` }}
                data-testid={`ack-alert-${a.alert_id}`}
              >
                <CheckCircle size={10} className="inline mr-1" /> Acknowledge
              </button>
            </div>
          );
        })}
      </div>
    </div>
  )}

  {/* Export pastoral data */}
  <div className="flex justify-end mb-3">
    <button
      onClick={() => {
        const token = getToken();
        fetch(`${API_URL}/api/corporate/export/wellbeing`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'pastoral_export.csv'; a.click(); URL.revokeObjectURL(u); });
      }}
      className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
      style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="export-pastoral-csv"
    >
      <Download size={12} /> Export Pastoral CSV
    </button>
  </div>

  {/* Privacy banner */}
  <div className="flex items-center gap-2 px-4 py-2.5 mb-5 rounded-lg text-xs" style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }}>
    <AlertTriangle size={14} />
    <span>This view shows <strong>mood categories only</strong>. No conversation content is accessible.</span>
  </div>

  {pastoral ? (
    <>
      {/* Mood Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="pastoral-mood-summary">
        {[
          { key: 'thriving', label: 'Thriving', colour: '#2EAA5C', bg: '#F0FDF4' },
          { key: 'steady', label: 'Steady', colour: '#2563EB', bg: '#EFF6FF' },
          { key: 'watchful', label: 'Watchful', colour: '#F59E0B', bg: '#FFFBEB' },
          { key: 'needs_attention', label: 'Needs Attention', colour: '#DC2626', bg: '#FEF2F2' },
          { key: 'inactive', label: 'Inactive', colour: '#9CA3AF', bg: '#F9FAFB' },
        ].map(cat => (
          <div key={cat.key} className="rounded-xl p-4" style={{ backgroundColor: cat.bg, border: `1px solid ${cat.colour}25` }} data-testid={`pastoral-card-${cat.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.colour }} />
              <span className="text-xs font-semibold" style={{ color: cat.colour }}>{cat.label}</span>
            </div>
            <p className="text-3xl font-bold" style={{ color: cat.colour }}>{pastoral.mood_summary?.[cat.key] || 0}</p>
            <p className="text-[10px] mt-1" style={{ color: '#999' }}>students</p>
          </div>
        ))}
      </div>

      {/* Student Mood Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="pastoral-student-table">
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E8E8' }}>
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Team Wellbeing ({pastoral.period})</h3>
          <span className="text-[10px]" style={{ color: '#999' }}>{pastoral.total_students} students</span>
        </div>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
              <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Member</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Team</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Team Lead</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Mood</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Sessions</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {(pastoral.students || []).length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm" style={{ color: '#AAA' }}>No members enrolled yet.</td></tr>
            ) : (pastoral.students || []).map((s, i) => (
              <tr key={s.student_id || i} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid={`pastoral-row-${i}`}>
                <td className="px-5 py-3 text-sm font-medium" style={{ color: '#1A1209' }}>{s.first_name} {s.last_initial}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.grade || '-'}</span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.teacher || '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.colour }} />
                    <span className="text-xs font-medium capitalize" style={{ color: s.colour }}>
                      {s.category?.replace('_', ' ')}
                    </span>
                    {s.avg_mood !== null && (
                      <span className="text-[10px] font-mono" style={{ color: '#999' }}>({s.avg_mood})</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs font-bold" style={{ color: '#333' }}>{s.sessions_30d}</td>
                <td className="px-4 py-3 text-xs" style={{ color: '#999' }}>
                  {s.last_active ? new Date(s.last_active).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Class OCEAN Profile Aggregates */}
      {oceanAggregates && oceanAggregates.students_with_profile > 0 && (
        <div className="bg-white rounded-xl overflow-hidden mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="pastoral-ocean-aggregates">
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E8E8' }}>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Class Social Profile</h3>
            <span className="text-[10px]" style={{ color: '#999' }}>{oceanAggregates.students_with_profile} of {oceanAggregates.total_students} students profiled</span>
          </div>
          <div className="p-5">
            <p className="text-xs mb-4" style={{ color: '#888' }}>Aggregated class averages. Individual team member profiles are never shown.</p>
            {[
              { key: 'openness', label: 'Curiosity', color: '#7B4FD8' },
              { key: 'conscientiousness', label: 'Follow-through', color: '#2563EB' },
              { key: 'extraversion', label: 'Social Energy', color: '#F5831F' },
              { key: 'agreeableness', label: 'Empathy', color: '#2EAA5C' },
              { key: 'neuroticism', label: 'Emotional Sensitivity', color: '#EC4899' },
            ].map((trait) => {
              const avg = oceanAggregates.averages?.[trait.key] || 0;
              const dist = oceanAggregates.distribution?.[trait.key] || {};
              const pct = Math.max(0, Math.min(100, (avg / 5) * 100));
              return (
                <div key={trait.key} className="mb-4" data-testid={`pastoral-ocean-${trait.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{trait.label}</span>
                    <span className="text-[10px]" style={{ color: '#999' }}>avg {avg}/5</span>
                  </div>
                  {/* Average bar */}
                  <div className="w-full h-2.5 rounded-full mb-1" style={{ backgroundColor: '#F0F0F0' }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: trait.color }} />
                  </div>
                  {/* Distribution stacked bar */}
                  <div className="relative group">
                    <div className="flex h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F0F0' }}>
                      {dist.low > 0 && <div style={{ width: `${dist.low}%`, backgroundColor: '#94A3B8' }} title={`Low: ${dist.low}%`} />}
                      {dist.medium > 0 && <div style={{ width: `${dist.medium}%`, backgroundColor: trait.color + '60' }} title={`Medium: ${dist.medium}%`} />}
                      {dist.high > 0 && <div style={{ width: `${dist.high}%`, backgroundColor: trait.color }} title={`High: ${dist.high}%`} />}
                    </div>
                    <div className="hidden group-hover:flex gap-3 mt-1 text-[9px]" style={{ color: '#999' }}>
                      <span>Low (1-2): {dist.low || 0}%</span>
                      <span>Medium (3): {dist.medium || 0}%</span>
                      <span>High (4-5): {dist.high || 0}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  ) : (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Heart size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm" style={{ color: '#999' }}>Pastoral data is available once students are enrolled and active.</p>
    </div>
  )}
</div>
  );
};

export default WellbeingTab;
